import { useState } from 'react';
import PropTypes from 'prop-types';
import { AutoAwesome as AIIcon } from '@mui/icons-material';
import classNames from 'classnames';
import LOGIN_TYPE from '../../constants/loginType';
import { getLoginDetailFromSession } from '../../helpers/sessionDetails';
import URL from '../../constants/urls';
import NEXT_ROUTES from '../../../next.routes';
import UI from '../../constants/ui';

const servicesData = {
  candidates: {
    title: UI.SERVICES_FOR_CANDIDATES,
    icon: '👥',
    services: [
      { to: URL.FIND_JOBS, label: UI.SERVICES_FIND_JOBS, icon: '💼' },
      {
        to: NEXT_ROUTES.CANDIDATE_ASSESSMENTS,
        label: UI.ASSESSMENTS,
        icon: '✅',
      },
      { to: NEXT_ROUTES.CANDIDATE_EVENTS, label: UI.EVENTS, icon: '📅' },
      {
        to: NEXT_ROUTES.CANDIDATE_MOCKINTERVIEW,
        label: UI.MOCK_INTERVIEWS,
        icon: '🎤',
      },
      { to: URL.AI_TOOLS, label: UI.AI_SERVICES, icon: <AIIcon /> },
    ],
  },
  employers: {
    title: UI.SERVICES_FOR_EMPLOYERS,
    icon: '🏢',
    services: [
      { to: URL.FIND_CANDIDATES, label: UI.SERVICES_FIND_TALENT, icon: '👥' },
      {
        to: NEXT_ROUTES.EMPLOYER_JOB_POSTING,
        label: UI.SERVICES_JOB_POSTING,
        icon: '📄',
      },
      {
        to: NEXT_ROUTES.EMPLOYER_ASSESSMENT,
        label: UI.ASSESSMENTS,
        icon: '📊',
      },
      {
        to: NEXT_ROUTES.EMPLOYER_INTERVIEW,
        label: UI.SERVICES_AI_INTERVIEWS,
        icon: '📝',
      },
      { to: NEXT_ROUTES.EMPLOYER_EVENTS, label: UI.EVENTS, icon: '📅' },
    ],
  },
  universities: {
    title: UI.SERVICES_FOR_UNIVERSITIES,
    icon: '🎓',
    services: [
      {
        to: NEXT_ROUTES.UNIVERSITY_CONNECT,
        label: UI.SERVICES_UNIVERSITY_CONNECT,
        icon: '🎓',
      },
    ],
  },
};

function ServicesDropdown({ onSelectNavOption, KEBAB_LINK }) {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const loggedInUserRole = getLoginDetailFromSession()?.role;

  const getRoleSpecificServices = () => {
    if (loggedInUserRole === LOGIN_TYPE.CANDIDATE) {
      return ['candidates'];
    }
    if (loggedInUserRole === LOGIN_TYPE.EMPLOYER) {
      return ['employers'];
    }
    if (loggedInUserRole === LOGIN_TYPE.UNIVERSITY) {
      return ['universities'];
    }
    return ['candidates', 'employers', 'universities'];
  };

  const toggleServices = () => {
    setServicesOpen((prev) => !prev);
    if (servicesOpen) {
      setExpandedSection(null);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const renderServiceDropdown = (sectionKey) => {
    const section = servicesData[sectionKey];
    const isExpanded = expandedSection === sectionKey;

    return (
      <div key={sectionKey} style={{ marginBottom: '8px' }}>
        <div
          className="service-dropdown-header"
          onClick={() => toggleSection(sectionKey)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            cursor: 'pointer',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>{section.icon}</span>
            <span
              style={{
                fontWeight: '600',
                fontSize: '14px',
                color: '#333',
              }}
            >
              {section.title}
            </span>
          </div>
          <div
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              fontSize: '14px',
              color: '#666',
            }}
          >
            <svg width="12" height="8" viewBox="0 0 12 8" fill="currentColor">
              <path
                d="M1 1.5l5 5 5-5"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        <div
          style={{
            maxHeight: isExpanded ? '300px' : '0',
            overflow: 'hidden',
            transition: 'max-height 0.3s ease',
            marginBottom: isExpanded ? '8px' : '0',
          }}
        >
          <ul
            style={{
              listStyle: 'none',
              padding: '0 16px',
              margin: '0',
            }}
          >
            {section.services.map((service) => (
              <li key={service.to} style={{ marginBottom: '25px' }}>
                <div
                  onClick={() => onSelectNavOption(service.to)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '6px 10px',
                    textDecoration: 'none',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    fontSize: '16px',
                    marginBottom: '-10px',
                    cursor: 'pointer',
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#e3f2fd';
                    e.target.style.color = '#1976d2';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#fff';
                    e.target.style.color = '#555';
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.backgroundColor = '#e3f2fd';
                    e.currentTarget.style.color = '#1976d2';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.color = '#555';
                  }}
                  className={classNames(KEBAB_LINK)}
                >
                  <span style={{ fontSize: '16px' }}>{service.icon}</span>
                  {service.label}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Services Main Button */}
      <div
        className="service-dropdown-header"
        onClick={toggleServices}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          cursor: 'pointer',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '8px',
          transition: 'all 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '20px' }}>🛠️</span>
          <span
            style={{
              fontWeight: '600',
              fontSize: '16px',
              color: '#333',
            }}
          >
            {UI.SERVICES_MAIN_TITLE}
          </span>
        </div>
        <div
          style={{
            transform: servicesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            fontSize: '14px',
            color: '#666',
          }}
        >
          <svg width="12" height="8" viewBox="0 0 12 8" fill="currentColor">
            <path
              d="M1 1.5l5 5 5-5"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Services Content */}
      <div
        style={{
          maxHeight: servicesOpen ? '600px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
          marginBottom: servicesOpen ? '16px' : '0',
        }}
      >
        <div style={{ padding: '0 16px' }}>
          {getRoleSpecificServices().map((sectionKey) =>
            renderServiceDropdown(sectionKey)
          )}
        </div>
      </div>
    </div>
  );
}

export default ServicesDropdown;

ServicesDropdown.propTypes = {
  onSelectNavOption: PropTypes.func.isRequired,
  KEBAB_LINK: PropTypes.string.isRequired,
};
